import { getCurrentUser, loginWithGoogle as loginWithGoogleApi } from "@/api/auth";
import { useAuthStore } from "@/app/store/useAuthStore";
import { clearSession } from "@/shared/libs/clearSession";
import { CurrentUserResponse } from "@/shared/types";
import { ReactNode, useCallback, useEffect } from "react";
import { AuthContext } from "./AuthContext";
interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const { user, grant, isLoading, setAuth, setLoading, clearAuth } = useAuthStore();

  const validateAndSetUser = useCallback(
    (me: CurrentUserResponse) => {
      if (!me.email || (me.grant !== "admin" && me.grant !== "member")) {
        localStorage.removeItem("access_token");
        throw new Error("등록되지 않은 사용자입니다.");
      }
      setAuth({
        user: { email: me.email, name: me.name ?? null, picture: me.picture ?? null },
        grant: me.grant,
        memberId: me.id,
        phone: me.phone,
      });
    },
    [setAuth],
  );

  useEffect(() => {
    const initAuth = async () => {
      try {
        const token = localStorage.getItem("access_token");
        if (token) {
          const me = await getCurrentUser();
          validateAndSetUser(me);
        }
      } catch (error) {
        console.error("인증 초기화 실패:", error);
        clearAuth();
      } finally {
        setLoading(false);
      }
    };

    void initAuth();
  }, [validateAndSetUser, setLoading, clearAuth]);

  const loginWithGoogleCallBack = useCallback(
    async (idToken: string) => {
      setLoading(true);
      try {
        const response = await loginWithGoogleApi({ token: idToken, tokenType: "id_token" });
        localStorage.setItem("access_token", response.accessToken);
        const me = await getCurrentUser();
        validateAndSetUser(me);
      } catch (error) {
        localStorage.removeItem("access_token");
        clearAuth();
        console.error("Google 로그인 실패:", error);
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [setLoading, validateAndSetUser, clearAuth],
  );

  const logout = useCallback(() => {
    clearSession();
    clearAuth();
  }, [clearAuth]);

  return (
    <AuthContext.Provider
      value={{
        user,
        grant,
        isAuthenticated: !!user,
        isLoading,
        loginWithGoogleCallBack,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}
